import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  Book, Shield, AlertTriangle, CheckCircle, ChevronDown, ChevronUp,
  UserCheck, School, Baby, Globe, Search, Filter,
} from 'lucide-react';
import ObserverNav from '../components/ObserverNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ObserverRuleBook = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState([]);
  const [filterRole, setFilterRole] = useState('observer');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRule, setExpandedRule] = useState(null);
  const [expandedCategory, setExpandedCategory] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('observer_token');
    if (!token) { navigate('/observer/login'); return; }
    loadRules();
  }, [navigate]);

  const loadRules = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/rules`);
      setRules(res.data.rules || []);
    } catch (e) {
      console.error('Error loading rules:', e);
    } finally {
      setLoading(false);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'observer': return <UserCheck className="w-4 h-4" />;
      case 'principal': return <School className="w-4 h-4" />;
      case 'parent': return <Baby className="w-4 h-4" />;
      default: return <Globe className="w-4 h-4" />;
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'observer': return 'bg-purple-100 text-purple-700';
      case 'principal': return 'bg-blue-100 text-blue-700';
      case 'parent': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      case 'low': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getCategoryIcon = (category) => {
    if (category.toLowerCase().includes('safety')) return '🛡️';
    if (category.toLowerCase().includes('privacy')) return '🔒';
    if (category.toLowerCase().includes('session')) return '📹';
    if (category.toLowerCase().includes('quality')) return '⭐';
    if (category.toLowerCase().includes('payment')) return '💰';
    if (category.toLowerCase().includes('conduct')) return '🤝';
    return '📋';
  };

  const filteredRules = rules.filter(rule => {
    if (filterRole !== 'all' && rule.applies_to !== filterRole && rule.applies_to !== 'all') return false;
    if (searchTerm && !rule.title.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !rule.description.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const groupedRules = filteredRules.reduce((acc, rule) => {
    const cat = rule.category || 'General';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(rule);
    return acc;
  }, {});

  const categories = Object.keys(groupedRules).sort();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ObserverNav />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ObserverNav />

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Book className="w-6 h-6 text-purple-600" />
              Rule Book
            </h1>
            <p className="text-gray-600 text-sm mt-1">
              Guidelines and policies for a safe, professional Sanjaya experience
            </p>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6 border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    placeholder="Search rules..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-600">Filter:</span>
                <div className="flex gap-1">
                  {[
                    { value: 'all', label: 'All', icon: Globe },
                    { value: 'observer', label: 'Observer', icon: UserCheck },
                    { value: 'principal', label: 'Principal', icon: School },
                    { value: 'parent', label: 'Parent', icon: Baby },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setFilterRole(opt.value)}
                      className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1 transition-colors ${
                        filterRole === opt.value
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <opt.icon className="w-4 h-4" />
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Rules Content */}
        {filteredRules.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="py-12 text-center">
              <Book className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No rules found</p>
              <p className="text-gray-400 text-sm">Try adjusting your search or filter criteria.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {/* Category pills */}
            <div className="flex flex-wrap gap-2">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setExpandedCategory(expandedCategory === cat ? null : cat)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors border ${
                    expandedCategory === cat
                      ? 'bg-purple-600 text-white border-purple-600'
                      : 'bg-white text-gray-700 border-gray-200 hover:border-purple-300'
                  }`}
                >
                  {getCategoryIcon(cat)} {cat} ({groupedRules[cat].length})
                </button>
              ))}
            </div>

            {/* Rules by Category */}
            {categories.map(category => (
              <Card
                key={category}
                className={`border-0 shadow-sm ${expandedCategory && expandedCategory !== category ? 'hidden' : ''}`}
              >
                <CardHeader
                  className="cursor-pointer hover:bg-gray-50 transition-colors py-4"
                  onClick={() => setExpandedCategory(expandedCategory === category ? null : category)}
                >
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-semibold text-gray-900 flex items-center gap-2">
                      <span className="text-xl">{getCategoryIcon(category)}</span>
                      {category}
                      <span className="text-sm font-normal text-gray-500">
                        ({groupedRules[category].length} rules)
                      </span>
                    </CardTitle>
                    {expandedCategory === category
                      ? <ChevronUp className="w-5 h-5 text-gray-400" />
                      : <ChevronDown className="w-5 h-5 text-gray-400" />}
                  </div>
                </CardHeader>

                <CardContent className={`space-y-3 pt-0 ${expandedCategory && expandedCategory !== category ? 'hidden' : ''}`}>
                  {groupedRules[category].map((rule, idx) => (
                    <div
                      key={rule.id}
                      className="border border-gray-100 rounded-lg overflow-hidden hover:shadow-sm transition-shadow"
                    >
                      <div
                        className="p-4 cursor-pointer"
                        onClick={() => setExpandedRule(expandedRule === rule.id ? null : rule.id)}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-7 h-7 rounded-md flex items-center justify-center text-white font-bold text-xs flex-shrink-0 ${
                            rule.priority === 'high' ? 'bg-red-500' :
                            rule.priority === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                          }`}>
                            {idx + 1}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <h4 className="font-semibold text-gray-900 text-sm">{rule.title}</h4>
                              <span className={`px-2 py-0.5 rounded-full text-xs flex items-center gap-1 ${getRoleBadgeColor(rule.applies_to)}`}>
                                {getRoleIcon(rule.applies_to)}
                                {rule.applies_to === 'all' ? 'Everyone' : rule.applies_to}
                              </span>
                              <span className={`px-2 py-0.5 rounded-full text-xs ${getPriorityBadge(rule.priority)}`}>
                                {rule.priority} priority
                              </span>
                            </div>
                            <p className="text-gray-600 text-sm">{rule.description}</p>
                          </div>
                          {expandedRule === rule.id
                            ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                        </div>
                      </div>

                      {expandedRule === rule.id && (
                        <div className="px-4 pb-4 space-y-3 bg-gray-50 border-t border-gray-100">
                          {rule.consequences && (
                            <div className="flex items-start gap-2 p-3 bg-red-50 rounded-lg mt-3">
                              <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="font-medium text-red-700 text-sm">Consequences</p>
                                <p className="text-red-600 text-sm">{rule.consequences}</p>
                              </div>
                            </div>
                          )}
                          {rule.examples && (
                            <div className="flex items-start gap-2 p-3 bg-green-50 rounded-lg">
                              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="font-medium text-green-700 text-sm">Examples & Guidelines</p>
                                <p className="text-green-600 text-sm">{rule.examples}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Contact */}
        <Card className="mt-6 border-0 shadow-sm bg-purple-50 border-purple-100">
          <CardContent className="p-5 text-center">
            <Shield className="w-8 h-8 text-purple-400 mx-auto mb-2" />
            <h3 className="text-base font-semibold text-gray-900 mb-1">Questions About Our Rules?</h3>
            <p className="text-gray-600 text-sm mb-2">
              Contact your assigned principal or reach out to our support team for clarification.
            </p>
            <p className="text-purple-700 text-sm font-medium">
              📧 support@sanjaya.in &nbsp;|&nbsp; 📞 +91 8291 188 188
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ObserverRuleBook;
